import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const PPCCampaignsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
            PPC Campaign Management
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Drive instant traffic and qualified leads with expertly managed pay-per-click advertising campaigns across Google, Facebook, and more.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
            PPC Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-flashlight-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Instant Results</h3>
              <p className="text-gray-600">Start driving traffic and leads immediately with targeted ad campaigns.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-target-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Precise Targeting</h3>
              <p className="text-gray-600">Reach your ideal customers based on location, interests, and search behavior.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white mb-4">
                <i className="ri-pie-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Measurable ROI</h3>
              <p className="text-gray-600">Track every dollar spent and optimize for maximum return on investment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Our PPC Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-google-line text-3xl text-yellow-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Google Ads</h4>
              <p className="text-sm text-gray-600">Search, display, and shopping campaigns</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-red-100 p-6 rounded-xl">
              <i className="ri-facebook-line text-3xl text-orange-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Facebook Ads</h4>
              <p className="text-sm text-gray-600">Social media advertising across Meta platforms</p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-pink-100 p-6 rounded-xl">
              <i className="ri-search-eye-line text-3xl text-red-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Keyword Research</h4>
              <p className="text-sm text-gray-600">Find the most profitable keywords for your business</p>
            </div>
            <div className="bg-gradient-to-br from-pink-100 to-purple-100 p-6 rounded-xl">
              <i className="ri-file-edit-line text-3xl text-pink-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Ad Creation</h4>
              <p className="text-sm text-gray-600">Compelling ad copy and creative design</p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-blue-100 p-6 rounded-xl">
              <i className="ri-line-chart-line text-3xl text-purple-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Performance Optimization</h4>
              <p className="text-sm text-gray-600">Continuous monitoring and improvement</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-teal-100 p-6 rounded-xl">
              <i className="ri-file-chart-line text-3xl text-blue-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Detailed Reporting</h4>
              <p className="text-sm text-gray-600">Transparent performance analytics and insights</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Follow Our PPC Success Stories</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Drive Instant Traffic?</h2>
          <p className="text-xl mb-8">Let's create PPC campaigns that deliver qualified leads and maximize your ROI!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-orange-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-orange-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PPCCampaignsPage;